import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReadRequestOptions } from '@/endpoints/requestOptions';
import { acquireOperationContext, isSessionTransition, operationRequirement } from '@/session/operation';
import type { SessionEndpoints } from '@/session/endpointManifest';
import { useEndpoints } from '@/session/useEndpoints';
import { useOperationRuns } from '@/session/useOperationRuns';
import type { EndpointResponse } from '@/types/endpoints';
import type { EpochMs } from '@/utils/orgCalendar';
import { utils } from '@/utils';

export type OrgMetricFetcher<P, R> = (endpoints: SessionEndpoints, params: P, options: ReadRequestOptions) => Promise<EndpointResponse<R>>;

export type OrgMetricState<R> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'ready'; result: R; timezone: string; requestedAt: EpochMs };

type Settled<P, R> =
  | { params: P; attempt: number; outcome: 'error'; message: string }
  | { params: P; attempt: number; outcome: 'ready'; result: R; timezone: string; requestedAt: EpochMs };

export const useOrgMetric = <P extends { timezone: string }, R>(
  fetcher: OrgMetricFetcher<P, R>,
  params: P | null,
  failureMessage: string
): OrgMetricState<R> => {
  const endpoints = useEndpoints();
  const runs = useOperationRuns();
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<Settled<P, R> | null>(null);
  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!params) return;

    const operation = acquireOperationContext(endpoints.organization);
    if (!operation.isScopeCurrent()) return;
    const run = runs.begin('org-metric', operation);
    const controller = new AbortController();
    const requestedAt = Date.now();
    const signal = AbortSignal.any([operation.scopeSignal, controller.signal]);

    void (async () => {
      try {
        const response = await fetcher(endpoints, params, {
          signal,
          operationRequirement: operationRequirement(operation, 'scope'),
        });
        if (utils.error.isCanceledResponse(response)) return;

        run.publish(() => {
          if (response.isError) {
            setSettled({ params, attempt, outcome: 'error', message: utils.error.errorCopy(failureMessage, response.errors.error) });
            return;
          }
          setSettled({ params, attempt, outcome: 'ready', result: response.result, timezone: params.timezone, requestedAt });
        });
      } catch (error) {
        if (controller.signal.aborted || isSessionTransition(error) || utils.error.isAbortError(error)) return;
        run.publish(() => setSettled({ params, attempt, outcome: 'error', message: utils.error.errorCopy(failureMessage) }));
      }
    })();

    return () => {
      run.invalidate();
      controller.abort();
    };
  }, [attempt, endpoints, failureMessage, fetcher, params, runs]);

  return useMemo((): OrgMetricState<R> => {
    if (!params) return { status: 'idle' };
    if (!settled || settled.params !== params || settled.attempt !== attempt) return { status: 'loading' };
    if (settled.outcome === 'error') return { status: 'error', message: settled.message, retry };
    return {
      status: 'ready',
      result: settled.result,
      timezone: settled.timezone,
      requestedAt: settled.requestedAt,
    };
  }, [attempt, params, retry, settled]);
};
